import { memo, useCallback } from "react";
import {
    GRID_ACTIONS_COLUMN_TYPE,
    type GridRowParams,
    type GridRowIdGetter
} from "@mui/x-data-grid";
import { Outlet } from "infrastructure/router";
import { TableGridMode } from "consts/table";
import { WebRoutes } from "consts/enpoints/web";
import { Filters } from "consts/onboarding";
import type { Application } from "features/onboarding/types";
import {
    selectIsApplicationsUninitialized,
    selectIsApplicationsLoading,
    selectApplicationsSlice
} from "features/onboarding/selectors";
import useActions from "features/onboarding/useActions";
import Resource from 'ui/layouts/Resource';
import Table, { TableProps } from "ui/widgets/Table";
import { TableActions, Toolbar } from './components';
import { ApplicationStatus } from './columns';
import {
    useApplicationTableResourceEvents,
    useApplicationTableResourceFilters
} from "./hooks";
import {
    AddMerchantManually,
    ApproveManuallyApplication,
    RejectApplication,
    MarkIdleApplication,
    DeleteApplication
} from "./actions";
import ApplicationTableResourceProvider from "./ApplicationTableResourceProvider";

type Props = Omit<
    TableProps,
    | 'mode'
    | 'selectIsTableUninitialized'
    | 'selectIsTableLoading'
    | 'selectTableDataSlice'
    | 'filterTypes'
> & {
    readonly resourceName: string;
};

const ApplicationsTableResource = ({ resourceName, ...restTableProps }: Props) => {
    const { resetApplications } = useActions();
    const { onRowClick } = useApplicationTableResourceEvents();
    const { filterTypes, filtersModelOverrides } = useApplicationTableResourceFilters();

    const getRowId: GridRowIdGetter<Application> = useCallback(({ pki }) => pki, []);

    return (
        <ApplicationTableResourceProvider>
            <Resource
                sx={{
                    position: 'relative'
                }}
                breadcrumbsProps={{
                    heading: 'Onboarding',
                    action: (
                        <AddMerchantManually />
                    ),
                    links: [
                        {
                            name: 'Applications',
                            href: WebRoutes.Onboarding.Applications.Index
                        },
                        {
                            name: resourceName
                        }
                    ],
                    sx: {
                        mb: 0
                    }
                }}
            >
                <Table
                    {...restTableProps}
                    initialState={{
                        pinnedColumns: {
                            right: [GRID_ACTIONS_COLUMN_TYPE]
                        }
                    }}
                    filterTypes={filterTypes}
                    mode={TableGridMode.OnboardingApplications}
                    selectIsTableUninitialized={selectIsApplicationsUninitialized}
                    selectIsTableLoading={selectIsApplicationsLoading}
                    selectTableDataSlice={selectApplicationsSlice}
                    resetState={resetApplications}
                    components={{
                        Toolbar
                    }}
                    onRowClick={onRowClick}
                    getRowId={getRowId}
                    columns={[
                        {
                            field: Filters.status,
                            headerName: 'Status',
                            minWidth: 170,
                            renderCell: ApplicationStatus
                        },
                        {
                            field: GRID_ACTIONS_COLUMN_TYPE,
                            type: GRID_ACTIONS_COLUMN_TYPE,
                            width: 50,
                            maxWidth: 50,
                            getActions: (rowParams: GridRowParams) => [
                                <TableActions {...rowParams} />
                            ]
                        }
                    ]}
                    pinnedColumnsSx={{
                        right: {
                            width: 50
                        }
                    }}
                    LayoutProps={{
                        sx: {
                            m: '24px 0px'
                        }
                    }}
                    FiltersProps={{
                        sx: {
                            m: 0
                        },
                        filtersModelOverrides
                    }}
                />
                <Outlet />
            </Resource>
            <ApproveManuallyApplication />
            <RejectApplication />
            <MarkIdleApplication />
            <DeleteApplication />
        </ApplicationTableResourceProvider>
    );
};

export default memo(ApplicationsTableResource);
